import {view, Icon} from '@sail/ui';
import {cancel, check, trial} from '@sail/icons/react/Icon';
import type {IconAsset} from '@sail/icons/types';
import {
  StepProgressIndicator,
  type StepStatus,
} from 'src/v2/components/common/StepProgressIndicator';
import {Text} from 'src/v2/components/common/Text';
import {BrandedButton} from 'src/v2/components/common/BrandedButton';
import {useSelector} from 'src/v2/state/store';
import {defineMessages, type MessageDescriptor, useIntl} from 'react-intl';
import {urlRedirect} from '@stripe-internal/safe-links';
import {COLORS} from 'src/v2/util/color';

const messages = defineMessages({
  processingTitle: {
    id: 'crypto.payins.v2.any_crypto.processing.title',
    description: 'Title for relay payment processing screen',
    defaultMessage: 'Your payment is being processed.',
  },
  processingSubtitle: {
    id: 'crypto.payins.v2.any_crypto.processing.subtitle',
    description: 'Subtitle for relay payment processing screen',
    defaultMessage:
      "It usually takes 15–30 minutes. You can safely close this window. We'll continue processing in the background.",
  },
  successTitle: {
    id: 'crypto.payins.v2.any_crypto.processing.success_title',
    description: 'Title when relay payment is complete',
    defaultMessage: 'Your payment is complete.',
  },
  successSubtitle: {
    id: 'crypto.payins.v2.any_crypto.processing.success_subtitle',
    description: 'Subtitle when relay payment is complete',
    defaultMessage: 'You can now view your updated balance.',
  },
  failedTitle: {
    id: 'crypto.payins.v2.any_crypto.processing.failed_title',
    description: 'Title when relay payment has failed',
    defaultMessage: 'Payment failed.',
  },
  failedSubtitle: {
    id: 'crypto.payins.v2.any_crypto.processing.failed_subtitle',
    description: 'Subtitle when relay payment has failed',
    defaultMessage:
      'This is usually due to insufficient funds or a network issue. Any funds deducted will be refunded. Start a new payment to try again.',
  },
  step1Label: {
    id: 'crypto.payins.v2.any_crypto.processing.step1',
    description: 'Label for deposit detecting step',
    defaultMessage: 'Payment detection',
  },
  step2Label: {
    id: 'crypto.payins.v2.any_crypto.processing.step2',
    description: 'Label for deposit delivering step',
    defaultMessage: 'Payment processing',
  },
  step2FailedLabel: {
    id: 'crypto.payins.v2.any_crypto.processing.step2_failed',
    description: 'Label for failed deposit delivery step',
    defaultMessage: 'Payment not delivered',
  },
  backButton: {
    id: 'crypto.payins.v2.any_crypto.processing.back_button',
    description: 'Button to return to merchant site',
    defaultMessage: 'Back to {businessName}',
  },
});

type Variant = 'processing' | 'success' | 'failed';

type VariantConfig = {
  icon: IconAsset;
  iconSize: string;
  iconColor: string;
  iconBackground: string;
  title: MessageDescriptor;
  subtitle: MessageDescriptor;
};

const VARIANT_CONFIG: Record<Variant, VariantConfig> = {
  processing: {
    icon: trial,
    iconSize: '20px',
    iconColor: COLORS.iconDefault,
    iconBackground: COLORS.backgroundNeutral,
    title: messages.processingTitle,
    subtitle: messages.processingSubtitle,
  },
  success: {
    icon: check,
    iconSize: '23px',
    iconColor: COLORS.textFeedbackSuccess,
    iconBackground: COLORS.backgroundFeedbackSuccess,
    title: messages.successTitle,
    subtitle: messages.successSubtitle,
  },
  failed: {
    icon: cancel,
    iconSize: '20px',
    iconColor: COLORS.iconFeedbackCritical,
    iconBackground: COLORS.backgroundFeedbackCritical,
    title: messages.failedTitle,
    subtitle: messages.failedSubtitle,
  },
};

function getVariant(
  maxRelayRequestStatus: string | null,
  sessionState: string,
): Variant {
  if (
    maxRelayRequestStatus === 'failure' ||
    maxRelayRequestStatus === 'refunded'
  ) {
    return 'failed';
  }
  if (sessionState === 'fulfillment_complete') {
    return 'success';
  }
  return 'processing';
}

function getStepStatuses(
  maxRelayRequestStatus: string | null,
  sessionState: string,
): [StepStatus, StepStatus] {
  if (
    maxRelayRequestStatus === 'failure' ||
    maxRelayRequestStatus === 'refunded'
  ) {
    return ['completed', 'failed'];
  }
  if (sessionState === 'fulfillment_complete') {
    return ['completed', 'completed'];
  }
  if (
    sessionState === 'purchase_complete' ||
    maxRelayRequestStatus === 'pending' ||
    maxRelayRequestStatus === 'submitted'
  ) {
    return ['completed', 'inProgress'];
  }
  if (maxRelayRequestStatus === 'depositing') {
    return ['inProgress', 'pending'];
  }
  return ['inProgress', 'pending'];
}

export const AnyCryptoProcessingScreen = () => {
  const intl = useIntl();
  const sessionState = useSelector((state) => state.state);
  const maxRelayRequestStatus = useSelector(
    (state) => state.anyCryptoDeposit.maxRelayRequestStatus,
  );
  const returnUrl = useSelector((state) => state.paymentDetails.returnUrl);
  const businessName = useSelector(
    (state) => state.merchantBranding.businessName,
  );

  const variant = getVariant(maxRelayRequestStatus, sessionState);
  const config = VARIANT_CONFIG[variant];
  const [step1Status, step2Status] = getStepStatuses(
    maxRelayRequestStatus,
    sessionState,
  );

  const step2Label =
    variant === 'failed' ? messages.step2FailedLabel : messages.step2Label;

  return (
    <view.div
      data-testid="any-crypto-processing-screen"
      css={{stack: 'y', gap: 'large'}}
    >
      <view.div
        css={{
          stack: 'x',
          alignX: 'center',
          alignY: 'center',
          width: 'xxlarge',
          height: 'xxlarge',
          borderRadius: '50%',
          backgroundColor: config.iconBackground,
        }}
      >
        <Icon
          icon={config.icon}
          css={{
            width: config.iconSize,
            height: config.iconSize,
            color: config.iconColor,
          }}
        />
      </view.div>

      <view.div css={{stack: 'y', gap: 'xsmall'}}>
        <Text type="heading" css={{color: COLORS.textPrimary}}>
          {intl.formatMessage(config.title)}
        </Text>
        <Text type="body" css={{color: COLORS.textPrimary}}>
          {intl.formatMessage(config.subtitle)}
        </Text>
      </view.div>

      <StepProgressIndicator
        steps={[
          {label: intl.formatMessage(messages.step1Label), status: step1Status},
          {label: intl.formatMessage(step2Label), status: step2Status},
        ]}
      />

      <BrandedButton onClick={() => urlRedirect(returnUrl)}>
        {intl.formatMessage(messages.backButton, {businessName})}
      </BrandedButton>
    </view.div>
  );
};
